import CrossIcon from '@atlaskit/icon/glyph/cross';
import Popup, { PopupComponentProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import React, { forwardRef, useEffect } from 'react';
import { useIntl } from 'react-intl-next';
import styled, { keyframes } from 'styled-components';

import { useAnalytics } from '@townsquare/analytics';

import { AtlasPrimaryOnboardingButton } from '../AtlasPrimaryOnboardingButton';
import { IntroPopupContainer } from '../style';

import IntroContent from './IntroContent';
import { useSaveBenefitsOnboarding } from './hooks';
import { messages } from './messages';

const CustomPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(
  // ref has a mismatched type with styled-component, we don't need it anyway
  ({ children, ref: _ref, ...props }, forwardRef) => (
    <div ref={forwardRef}>
      <StyledPopupComponent {...props}>{children}</StyledPopupComponent>
    </div>
  ),
);

export const IntroPopup = () => {
  const { shouldOpenByDefault, hasLoaded } = useSaveBenefitsOnboarding();
  const [isOpen, setIsOpen] = React.useState(false);
  const intl = useIntl();
  const analytics = useAnalytics();

  useEffect(() => {
    if (shouldOpenByDefault && hasLoaded) {
      setIsOpen(true);
    }
  }, [shouldOpenByDefault, hasLoaded]);

  const handleButtonClick = () => {
    void analytics.ui('button', 'clicked', { isOpening: !isOpen }, 'atlasBenefitsPopupTrigger');
    setIsOpen(!isOpen);
  };

  return hasLoaded ? (
    <IntroPopupContainer>
      <Popup
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        content={IntroContent}
        popupComponent={CustomPopupContainer}
        placement="top-start"
        shouldUseCaptureOnOutsideClick
        trigger={triggerProps => (
          <div {...triggerProps}>
            <AtlasPrimaryOnboardingButton
              isSelected={isOpen}
              icon={
                isOpen ? (
                  <CrossIcon
                    label={intl.formatMessage(messages.closeIcon)}
                    primaryColor={token('color.text')}
                    size="small"
                  />
                ) : undefined
              }
              onClick={handleButtonClick}
              label={
                isOpen
                  ? intl.formatMessage(messages.closeButton)
                  : intl.formatMessage(messages.earlyAccessToGoalsPopupButton)
              }
            />
          </div>
        )}
      />
    </IntroPopupContainer>
  ) : null;
};

const slideInAnimation = keyframes({
  '0%': {
    bottom: '-100%',
    opacity: 0.1,
  },
  '100%': {
    opacity: 1,
  },
});

const StyledPopupComponent = styled.div({
  backgroundColor: token('elevation.surface.overlay'),
  display: 'block',
  flex: '1 1 auto',
  borderRadius: '3px',
  boxShadow: token('elevation.shadow.overlay'),
  marginBottom: token('space.150'),
  ':focus': {
    outline: 'none',
  },
  animationName: slideInAnimation,
  animationDuration: '400ms',
});
